// src/Pages/Home.js
import React from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, FileDown, Github } from "lucide-react";

// Article modules
import suricataBench from "../articles/suricata-bench";
import earthwormPygmyGoat from "../articles/earthworm-pygmy-goat";
import jaBenchAnyrunQuasar from "../articles/ja-bench-anyrun-quasar";
import griftGithubSeoMalware from "../articles/grift-github-seo-malware";

export default function Home() {
  const navigate = useNavigate();

  // helper to make any tile area navigate SPA-style
  const go = (to) => (e) => {
    e.preventDefault();
    navigate(to);
  };
  const onKeyGo = (to) => (e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      navigate(to);
    }
  };

  const featured = [
    {
      title: griftGithubSeoMalware.title,
      path: "/articles/grift-github-seo-malware",
      image: griftGithubSeoMalware.image,
      summary: griftGithubSeoMalware.summary,
      tags: griftGithubSeoMalware.tags,
      repoUrl: griftGithubSeoMalware.repoUrl,
      category: griftGithubSeoMalware.category || "Threat Hunting",
    },
    {
      title: jaBenchAnyrunQuasar.title,
      path: "/articles/ja-bench-anyrun-quasar",
      image: jaBenchAnyrunQuasar.image,
      summary: jaBenchAnyrunQuasar.summary,
      tags: jaBenchAnyrunQuasar.tags,
      repoUrl: jaBenchAnyrunQuasar.repoUrl,
      category: "Tool",
    },
    {
      title: earthwormPygmyGoat.title,
      path: "/articles/earthworm-pygmy-goat",
      image: earthwormPygmyGoat.image,
      summary: earthwormPygmyGoat.summary,
      tags: earthwormPygmyGoat.tags,
      repoUrl: earthwormPygmyGoat.repoUrl,
      category: "Detection Engineering",
    },
    {
      title: suricataBench.title,
      path: "/articles/suricata-bench",
      image: suricataBench.image,
      summary: suricataBench.summary,
      tags: suricataBench.tags,
      repoUrl: suricataBench.repoUrl,
      category: "Tool",
    },
  ];

  return (
    <div className="mx-auto max-w-6xl px-4 py-10">
      {/* Hero */}
      <section className="text-center mb-12">
        <h1 className="text-4xl md:text-5xl font-extrabold text-slate-100">
          Brimerica
        </h1>
        <p className="mt-3 text-slate-300 max-w-2xl mx-auto">
          Hunting Threats, Engineering Detections, Delivering Security.
        </p>

        <div className="mt-6 flex items-center justify-center gap-3">
          <button
            type="button"
            onClick={go("/articles")}
            className="inline-flex items-center gap-2 rounded-lg bg-cyan-700 hover:bg-cyan-600 px-4 py-2 font-semibold text-white transition-colors"
          >
            Browse Articles
            <ArrowRight className="w-4 h-4" />
          </button>

          <a
            href="/Brian_Williams_Resume_w.pdf"
            download
            className="inline-flex items-center gap-2 rounded-lg border border-slate-700 px-4 py-2 font-semibold text-slate-200 hover:bg-slate-800 transition-colors"
          >
            <FileDown className="w-4 h-4" />
            Download Resume
          </a>
        </div>
      </section>

      {/* Work Highlights */}
      <section>
        <h2 className="text-2xl font-bold text-slate-100 mb-6">
          Work Highlights
        </h2>

        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-8">
          {featured.map((a) => (
            <article
              key={a.path}
              className="rounded-2xl overflow-hidden border border-slate-800 bg-slate-900 shadow-md shadow-slate-950/30"
            >
              {/* Whole image area is keyboard+mouse navigable (SPA) */}
              <div
                role="link"
                tabIndex={0}
                onClick={go(a.path)}
                onKeyDown={onKeyGo(a.path)}
                className="relative block cursor-pointer"
                aria-label={`Open article: ${a.title}`}
              >
                <img
                  src={a.image}
                  alt={a.title}
                  className="w-full h-56 object-cover"
                />
                {a.category && (
                  <span className="absolute top-3 left-3 inline-flex items-center rounded-full bg-emerald-600 px-3 py-1 text-xs font-semibold text-white">
                    {a.category}
                  </span>
                )}
              </div>

              <div className="p-4">
                {/* Title (SPA) */}
                <button
                  type="button"
                  onClick={go(a.path)}
                  className="text-left text-lg font-semibold text-white hover:underline"
                >
                  {a.title}
                </button>

                {a.summary && (
                  <p className="mt-2 text-slate-300 text-sm">{a.summary}</p>
                )}

                {Array.isArray(a.tags) && a.tags.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-2">
                    {a.tags.map((t) => (
                      <span
                        key={t}
                        className="text-xs rounded-full bg-slate-800 px-2 py-1 text-slate-300 border border-slate-700"
                      >
                        {t}
                      </span>
                    ))}
                  </div>
                )}

                <div className="mt-4 flex items-center justify-between">
                  {/* CTA (SPA) */}
                  <button
                    type="button"
                    onClick={go(a.path)}
                    className="inline-flex items-center gap-1.5 text-cyan-400 hover:text-cyan-300 font-medium"
                  >
                    Read Article <ArrowRight className="w-4 h-4" />
                  </button>

                  {/* GitHub stays external */}
                  {a.repoUrl && (
                    <a
                      href={a.repoUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-slate-300 hover:text-white"
                      aria-label="GitHub repository"
                      title="View on GitHub"
                    >
                      <Github className="w-4 h-4" />
                      <span className="text-xs hidden sm:inline">GitHub</span>
                    </a>
                  )}
                </div>
              </div>
            </article>
          ))}
        </div>
      </section>
    </div>
  );
}
